import { Button, Checkbox, type Column } from "@legal-500/legal500-design-system";
import type { Award } from "../../api/awards.types.js";
import { FirmLawyerCell } from "./components/FirmLawyerCell.js";
import { JurisdictionChips } from "./components/JurisdictionChips.js";
import { PublishStatusChip } from "./components/PublishStatusChip.js";
import { StatusCell } from "./components/StatusCell.js";
import { TypeCategoryCell } from "./components/TypeCategoryCell.js";

/**
 * Columns for the Awards list.
 *
 * Headers are Title Case, not the mock's UPPERCASE: the design system's
 * `Table.module.css` already applies `text-transform: uppercase` to every `th`,
 * and the app has ~300 Title Case header literals against 7 all-caps ones.
 *
 * `wrap: true` on the three composite columns is the sanctioned escape from
 * `DataTable.module.css`, which forces `white-space: nowrap` on every `td`/`th`;
 * the design system turns the flag into an inline `white-space: normal` that
 * out-specifies it. 36 columns in this app already do this. The alternative —
 * Commentaries' `:global(.tableWrapper td) { white-space: normal !important }` —
 * is a page-wide hammer that would also unwrap the simple columns.
 *
 * Every `accessor` is a real key of `Award`, so the design system's fallback
 * (`String(row[column.accessor])`) would still print something sane if a
 * `render` were ever dropped. The composite cells accessor the field their first
 * line shows.
 *
 * The first column is a selection checkbox, and it exists for one consumer: the
 * export, which takes the selected ids. The header cell holds the page-level
 * checkbox, which is why the header is a node rather than a label — the design
 * system types `header` as a string, and the `as unknown as string` cast is the
 * same one DataTable itself uses for its sortable headers.
 *
 * Sorting is deliberately absent. The two "needs linking" marks stay inside the
 * Firm / Lawyer cell rather than joining the Actions column: each belongs to the
 * line whose id is missing, not to the row — which is the whole point on a
 * half-matched row, where one of the two is resolved. The Actions column, always
 * last, holds only what acts on the row as a whole, and exists only for a user
 * who may write.
 */

export type AwardColumnsOptions = {
  /** Ids of the selected awards, across every page. Owned by the page, which is
   * what the export reads it from. */
  selectedIds: ReadonlySet<string>;
  /** Whether every row on the current page is selected — what the header
   * checkbox shows, and what decides whether its next click adds or removes. */
  allOnPageSelected: boolean;
  onToggleAward: (id: string) => void;
  onTogglePage: () => void;
  /**
   * Resolves a taxonomy country id to its chip label. Owned by the page via
   * `useCountryNames()`, which holds the single region-countries subscription
   * for the whole table.
   */
  getCountryLabel: (countryId: number) => string;
  /**
   * Asks the page to open its Link Firm modal for this row. Optional, and absent
   * for a user without `staff.awards.write` — that is what turns the firm mark
   * back into a plain indicator.
   *
   * The cell stays dumb: the modal, the selected row and the refetch afterwards
   * all belong to the page, because the fix changes which rows the server would
   * return and a cell cannot own that.
   *
   * Must be referentially stable — the page memoises this factory, so a fresh
   * closure per render would rebuild every column on every render.
   */
  onLinkFirm?: (award: Award) => void;
  /**
   * Asks the page to open its Link Lawyer modal for this row, on exactly the
   * same terms as `onLinkFirm` — absent for a user without `staff.awards.write`,
   * and referentially stable for the same memo.
   *
   * The *other* reason a row may not offer it — no linked firm to scope the
   * contact search to — is not decided here. The cell already holds `companyId`
   * and decides there, so this option stays a pure statement about permission.
   */
  onLinkLawyer?: (award: Award) => void;
  /**
   * Opens the edit form for this row. Optional on the same terms as the two
   * above: absent for a user without `staff.awards.write`, and then the whole
   * Actions column is left out rather than rendered empty.
   */
  onEdit?: (award: Award) => void;
};

/**
 * A factory rather than a module constant: the Jurisdictions column closes over
 * the country-name lookup, and that lookup lives in page state. The page
 * memoises the call on `getCountryLabel`, so the column array changes identity
 * exactly when the resolved names do — once, when the tree lands.
 */
export function createAwardColumns({
  selectedIds,
  allOnPageSelected,
  onToggleAward,
  onTogglePage,
  getCountryLabel,
  onLinkFirm,
  onLinkLawyer,
  onEdit,
}: AwardColumnsOptions): Column<Award>[] {
  const columns: Column<Award>[] = [
    {
      header: (
        <Checkbox
          aria-label="Select all awards on this page"
          checked={allOnPageSelected}
          onChange={onTogglePage}
        />
      ) as unknown as string,
      accessor: "id",
      render: (_value, row) => (
        <Checkbox
          aria-label={`Select ${row.awardTypeName} for ${row.firmName ?? row.firmNameRaw}`}
          checked={selectedIds.has(row.id)}
          onChange={() => onToggleAward(row.id)}
        />
      ),
    },
    {
      header: "Product",
      accessor: "productName",
      render: (_value, row) => row.productName || "—",
    },
    {
      header: "Year",
      accessor: "year",
      render: (_value, row) => String(row.year),
    },
    {
      header: "Type of Award / Category",
      accessor: "awardTypeName",
      wrap: true,
      render: (_value, row) => (
        <TypeCategoryCell awardTypeName={row.awardTypeName} categoryName={row.categoryName} />
      ),
    },
    {
      header: "Award Series",
      accessor: "seriesName",
      render: (_value, row) => row.seriesName || "—",
    },
    {
      header: "Firm / Lawyer",
      accessor: "firmName",
      wrap: true,
      // Both ids travel with the names: the cell branches on `companyId` /
      // `contactId` — the thing that actually means "matched" — and falls back to
      // the verbatim spreadsheet text when one is missing.
      render: (_value, row) => (
        <FirmLawyerCell
          companyId={row.companyId}
          firmName={row.firmName}
          firmNameRaw={row.firmNameRaw}
          contactId={row.contactId}
          lawyerName={row.lawyerName}
          lawyerNameRaw={row.lawyerNameRaw}
          // Bound to the row here rather than passed down as `(award) => void`:
          // the cell's job is to report a click, not to know which award it is
          // reporting about. Undefined stays undefined, which is what keeps the
          // mark a span for a user who may not write.
          onLinkFirm={onLinkFirm ? () => onLinkFirm(row) : undefined}
          onLinkLawyer={onLinkLawyer ? () => onLinkLawyer(row) : undefined}
        />
      ),
    },
    {
      header: "Status",
      accessor: "status",
      render: (_value, row) => <StatusCell status={row.status} shortlistTag={row.shortlistTag} />,
    },
    {
      header: "Publish Status",
      accessor: "state",
      render: (_value, row) => <PublishStatusChip state={row.state} />,
    },
    {
      header: "Jurisdictions",
      accessor: "jurisdictionCountryIds",
      wrap: true,
      render: (_value, row) => (
        <JurisdictionChips countryIds={row.jurisdictionCountryIds} getLabel={getCountryLabel} />
      ),
    },
  ];

  if (onEdit) {
    columns.push({
      header: "Actions",
      accessor: "id",
      render: (_value, row) => (
        <Button
          variant="outlined"
          color="default"
          size="small"
          aria-label={`Edit ${row.awardTypeName} (${row.year})`}
          onClick={event => {
            event.stopPropagation();
            onEdit(row);
          }}
        >
          Edit
        </Button>
      ),
    });
  }

  return columns;
}
